/**
 * Utilitários para manipulação de datas - Customer
 * 
 * REGRA FUNDAMENTAL:
 * - Campos DATE (YYYY-MM-DD): são apenas datas, sem timezone. Parsear como LOCAL.
 * - Campos DATETIME (com T ou hora): têm timezone, parsear como UTC/ISO.
 */

/**
 * Parseia string de data de forma segura.
 * - DATE (YYYY-MM-DD): trata como data LOCAL (sem conversão UTC)
 * - DATETIME (com T ou hora): trata como UTC/ISO
 */
function parseDate(date: string | Date | null | undefined): Date | null {
  if (!date) return null;
  if (date instanceof Date) {
    // Se é Date, verificar se parece ser um campo DATE (hora é 00:00:00 UTC)
    // Se sim, tratar como LOCAL para evitar problemas de timezone
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const seconds = date.getUTCSeconds();
    const ms = date.getUTCMilliseconds();
    
    // Se é meia-noite UTC e não tem frações, provavelmente é um campo DATE
    if (hours === 0 && minutes === 0 && seconds === 0 && ms === 0) {
      const year = date.getUTCFullYear();
      const month = date.getUTCMonth();
      const day = date.getUTCDate();
      return new Date(year, month, day);
    }
    
    return isNaN(date.getTime()) ? null : date;
  }
  
  // Se é string, verificar formato
  // Se é apenas data (YYYY-MM-DD), parsear como LOCAL
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  
  // Se é ISO com hora mas termina em T00:00:00.000Z, tratar como DATE LOCAL
  if (/^\d{4}-\d{2}-\d{2}T00:00:00(\.000)?Z$/.test(date)) {
    const [year, month, day] = date.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  
  // Se tem hora/timezone, parsear normalmente
  const d = new Date(date);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Formata uma data (compatível com assinatura antiga)
 * @deprecated Use formatDateBR ou formatDateTimeBR diretamente
 */
export function formatDate(
  date: string | Date | null | undefined,
  _userTimezone: string = 'America/Sao_Paulo',
  formatString: string = 'dd/MM/yyyy HH:mm',
): string {
  void _userTimezone;
  const d = parseDate(date);
  if (!d) return 'N/A';
  
  // Detectar formato desejado e chamar função apropriada
  if (formatString === 'dd/MM/yyyy') {
    return formatDateOnly(date);
  }
  if (formatString === 'dd/MM/yyyy HH:mm:ss') {
    return formatDateTime(date);
  }
  if (formatString === 'HH:mm') {
    return formatTime(date);
  }
  if (formatString.includes('MMMM')) {
    return formatDateLong(date);
  }
  
  // Default: data e hora
  return formatDateTimeBR(date);
}

/**
 * Formata apenas a data (sem hora)
 */
export function formatDateOnly(
  date: string | Date | null | undefined,
  _userTimezone: string = 'America/Sao_Paulo',
): string {
  void _userTimezone;
  return formatDateBR(date);
}

/**
 * Formata data e hora completa
 */
export function formatDateTime(
  date: string | Date | null | undefined,
  _userTimezone: string = 'America/Sao_Paulo',
): string {
  void _userTimezone;
  return formatDateTimeFullBR(date);
}

/**
 * Formata apenas a hora
 */
export function formatTime(
  date: string | Date | null | undefined,
  _userTimezone: string = 'America/Sao_Paulo',
): string {
  void _userTimezone;
  const d = parseDate(date);
  if (!d) return 'N/A';
  
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  
  return `${hours}:${minutes}`;
}

/**
 * Formata data no formato longo (ex: "15 de dezembro de 2024")
 */
export function formatDateLong(
  date: string | Date | null | undefined,
  _userTimezone: string = 'America/Sao_Paulo',
): string {
  void _userTimezone;
  const d = parseDate(date);
  if (!d) return 'N/A';
  
  const meses = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ];
  
  return `${d.getDate()} de ${meses[d.getMonth()]} de ${d.getFullYear()}`;
}

/**
 * Formata data e hora no formato curto (ex: "15/12/2024 14:30")
 */
export function formatDateShort(
  date: string | Date | null | undefined,
  _userTimezone: string = 'America/Sao_Paulo',
): string {
  void _userTimezone;
  return formatDateTimeBR(date);
}

// ============================================
// Funções principais (novas)
// ============================================

/**
 * Formata data para exibição no formato brasileiro (dd/MM/yyyy)
 */
export function formatDateBR(date: string | Date | null | undefined): string {
  const d = parseDate(date);
  if (!d) return 'N/A';
  
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  
  return `${day}/${month}/${year}`;
}

/**
 * Formata data e hora no formato brasileiro (dd/MM/yyyy HH:mm)
 */
export function formatDateTimeBR(date: string | Date | null | undefined): string {
  const d = parseDate(date);
  if (!d) return 'N/A';
  
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

/**
 * Formata data e hora completa no formato brasileiro (dd/MM/yyyy HH:mm:ss)
 */
export function formatDateTimeFullBR(date: string | Date | null | undefined): string {
  const d = parseDate(date);
  if (!d) return 'N/A';
  
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
}

/**
 * Converte data para formato de input HTML (yyyy-MM-dd)
 */
export function toInputDate(date: string | Date | null | undefined): string {
  const d = parseDate(date);
  if (!d) return '';
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Converte data para formato de input HTML datetime-local
 */
export function toInputDateTime(date: string | Date | null | undefined): string {
  const d = parseDate(date);
  if (!d) return '';
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Parse seguro de data para uso externo
 */
export function safeParseDate(date: string | Date | null | undefined): Date | null {
  return parseDate(date);
}

/**
 * Retorna a data atual no fuso horário de Brasília (America/Sao_Paulo) no formato YYYY-MM-DD.
 * Essencial para evitar distorções de virada de dia após as 21:00h UTC.
 */
export function getHojeISO(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
}

/**
 * Retorna data futura (ou passada) com N dias de deslocamento ancorada estritamente no fuso de Brasília (YYYY-MM-DD).
 */
export function getDeslocamentoDiasISO(dias: number): string {
  const hojeStr = getHojeISO();
  const [ano, mes, dia] = hojeStr.split('-').map(Number);
  const d = new Date(ano, mes - 1, dia);
  d.setDate(d.getDate() + dias);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Retorna objeto Date aproximado no fuso horário de Brasília
 */
export function getAgoraTimezoneSP(): Date {
  const agoraSP = new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' });
  return new Date(agoraSP);
}

/**
 * Retorna a data do primeiro dia do mês atual (YYYY-MM-01) ancorada no fuso de Brasília.
 */
export function getPrimeiroDiaMesISO(): string {
  const hojeStr = getHojeISO();
  const [ano, mes] = hojeStr.split('-').map(Number);
  const m = String(mes).padStart(2, '0');
  return `${ano}-${m}-01`;
}

/**
 * Retorna a data do último dia do mês atual (YYYY-MM-DD) ancorada no fuso de Brasília.
 */
export function getUltimoDiaMesISO(): string {
  const hojeStr = getHojeISO();
  const [ano, mes] = hojeStr.split('-').map(Number);
  const d = new Date(ano, mes, 0);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

